import { isBefore } from 'date-fns';
import { fromDateString } from '@/common/utils/dateFormats';
import {
  ITEM_HEIGHT,
  ITEM_HEIGHT_COMPACT,
  ITEM_LANE_HEIGHT,
  ITEM_LANE_HEIGHT_COMPACT,
  MIN_ITEM_WIDTH,
} from '../TimelineItemConstants';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Day count between two ISO date strings, computed from UTC midnights so the
// result is timezone-independent (date-fns differenceInDays on local-parsed
// date strings can drift by a day across a DST boundary).
const utcDaysBetween = (from: string, to: string): number => {
  const fromDate = fromDateString(from);
  const toDate = fromDateString(to);
  if (!fromDate || !toDate) return 0;
  return Math.round((toDate.getTime() - fromDate.getTime()) / MS_PER_DAY);
};

export const clampStartDateToWindow = (
  normalizedStart: string | undefined,
  timelineStartDate: string,
): string => {
  if (normalizedStart && isBefore(normalizedStart, timelineStartDate)) {
    return timelineStartDate;
  }
  return normalizedStart ?? timelineStartDate;
};

export const clampEndDateToWindow = (
  normalizedFinish: string | undefined,
  timelineEndDate: string,
): string => {
  if (normalizedFinish && isBefore(normalizedFinish, timelineEndDate)) {
    return normalizedFinish;
  }
  return timelineEndDate;
};

export const calculateItemWidth = (
  startDateToUse: string,
  endDateToUse: string,
  dayWidth: number,
): number => {
  const numDaysInItem = utcDaysBetween(startDateToUse, endDateToUse) + 1;
  return Math.max(dayWidth * numDaysInItem, MIN_ITEM_WIDTH);
};

export const calculateUncertaintyTailWidth = ({
  latestFinishDateTime,
  finishDateToUse,
  timelineEndDate,
  dayWidth,
}: {
  latestFinishDateTime: string | null | undefined;
  finishDateToUse: string;
  timelineEndDate: string;
  dayWidth: number;
}): number => {
  if (!latestFinishDateTime) return 0;
  const normalizedLatest = latestFinishDateTime.split('T')[0];
  const clampedLatest = clampEndDateToWindow(normalizedLatest, timelineEndDate);
  const extraDays = utcDaysBetween(finishDateToUse, clampedLatest);
  if (extraDays <= 0) return 0;
  return extraDays * dayWidth;
};

export const getSingleLaneHeight = (isCompact: boolean): number =>
  isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;

export const getItemHeight = (isCompact: boolean): number =>
  isCompact ? ITEM_HEIGHT_COMPACT : ITEM_HEIGHT;

export const calculateMaxContainerHeight = (
  singleLaneHeight: number,
  maxChildOrder: number,
  hasChildren: boolean,
): number => {
  if (!hasChildren) return singleLaneHeight;
  const parentLane = 1;
  const childLanes = maxChildOrder + 1;
  return (parentLane + childLanes) * singleLaneHeight;
};

export const isOutsideTimelineWindow = (
  hasAnyPlannedDate: boolean,
  startDateToUse: string,
  endDateToUse: string,
  timelineStartDate: string,
  timelineEndDate: string,
): boolean => {
  if (!hasAnyPlannedDate) return true;
  const startsAfterWindow = isBefore(timelineEndDate, startDateToUse);
  const endsBeforeWindow = isBefore(endDateToUse, timelineStartDate);
  return startsAfterWindow || endsBeforeWindow;
};
